import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { v4 as uuid } from 'uuid'
import MergeTypeIcon from '@mui/icons-material/MergeType'
import { LoadingButton } from '@mui/lab'
import { Button, List, ListItem, ListItemText, Typography } from '@mui/material'
import { useResultsInvalidation } from '@editor/hooks'
import {
  DEFAULT_LINE_LIKE_SETTINGS,
  wallSettingsOf,
  WallSettings,
} from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { useMutation } from '@tanstack/react-query'
import DialogBase from '@ui/feedback/DialogBase'
import { Errors, Form } from '@ui/forms'
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { getModel, getVerticalTransmissionGraph } from '@queries'
import { createOpening, createRips } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import queryClient from 'src/state/client'
import { mergeWallSchema } from './schema'
import { mergeWalls } from './utils'

interface Props {
  wall1_guid: string
  wall2_guid: string
  handleCreate: (
    wall: {
      p1: { x: number; y: number }
      p2: { x: number; y: number }
      storey_idx: number
      placement: WallPlacement
      openings: Opening[]
    } & WallSettings,
  ) => Promise<ShapeObject>
  handleDelete: (wallGuid: string) => Promise<void>
  onSuccess?: () => void
}

interface MergeFormValues {
  wall1: ShapeObject
  wall2: ShapeObject
}

const MergeWallForm = ({
  wall1_guid,
  wall2_guid,
  handleCreate,
  handleDelete,
  onSuccess,
}: Props): ReactElement => {
  const { walls, rips } = useModelStore(state => state.model)
  const invalidateResults = useResultsInvalidation()
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<MergeFormValues | null>(null)

  // memos
  const wall1 = useMemo(() => {
    return walls.find(wall => wall.guid === wall1_guid)
  }, [walls, wall1_guid])

  const wall2 = useMemo(() => {
    return walls.find(wall => wall.guid === wall2_guid)
  }, [walls, wall2_guid])

  const defaultValues = useMemo(
    () => ({
      wall1: wall1,
      wall2: wall2,
    }),
    [wall1, wall2],
  )

  const { projectId }: { projectId?: string } = useParams()

  const { mutate, isLoading } = useMutation(
    async (formData: MergeFormValues) => {
      const snackbarWaitItemId = `merge-wall-form-${wall1_guid}-${wall2_guid}-pending-notification`
      enqueueSnackbar('Wände werden zusammengeführt. Dies kann einige Sekunden dauern.', {
        variant: 'info',
        persist: true,
        key: snackbarWaitItemId,
      })

      const mergeData = mergeWalls(
        formData.wall1 as ShapeObject,
        formData.wall2 as ShapeObject,
        rips,
      )

      // Delete both walls
      await handleDelete(wall1_guid)
      await handleDelete(wall2_guid)

      // Create the merged wall
      const newWall = await handleCreate({ ...mergeData.newWall, ...wallSettingsOf(wall1) })

      // Create openings for the merged wall
      for (const opening of mergeData.newWall.openings) {
        await createOpening.request(projectId as string, {
          element_guid: newWall.guid,
          shape: opening.shape,
        })
      }

      // Create additional rips on the merged wall (projected from both original walls)
      if (mergeData.newWall.additionalRips.length > 0) {
        const ripRequests: WallRipRequest[] = mergeData.newWall.additionalRips.map(rip => ({
          ...DEFAULT_LINE_LIKE_SETTINGS,
          guid: uuid(),
          wall_guid: newWall.guid,
          relative_position: rip.relative_position,
          rip_type: 'AdditionalRip',
        }))
        await createRips.request(projectId as string, ripRequests)
      }

      return { newWall, snackbarWaitItemId }
    },
    {
      onSuccess: async (data: { snackbarWaitItemId: SnackbarKey }) => {
        closeSnackbar(data.snackbarWaitItemId)
        await Promise.all([
          queryClient.invalidateQueries(getModel.getKey(projectId)),
          queryClient.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          queryClient.invalidateQueries(getModel.getKey(projectId)),
          queryClient.invalidateQueries(getModel.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Wände erfolgreich zusammengeführt', { variant: 'success' })
        onSuccess?.()
      },
      onError: (error: AxiosError) => {
        const snackbarWaitItemId = `merge-wall-form-${wall1_guid}-${wall2_guid}-pending-notification`
        closeSnackbar(snackbarWaitItemId)
        const errorMessage = buildErrorMessage(error, 'Fehler beim Zusammenführen der Wände')
        enqueueSnackbar(errorMessage, { variant: 'error' })
      },
    },
  )

  const handleSubmit = (values: MergeFormValues) => {
    // Show confirmation dialog instead of submitting directly
    setPendingFormData(values)
    setShowConfirmDialog(true)
  }

  const handleConfirm = () => {
    if (pendingFormData) {
      setShowConfirmDialog(false)
      mutate(pendingFormData)
      setPendingFormData(null)
    }
  }

  const handleCancel = () => {
    setShowConfirmDialog(false)
    setPendingFormData(null)
  }

  const sideEffects = [
    'Hinzugefügte Lasten',
    'Lastweiterleitung (Vertikal und Zug)',
    'Aufbau und QS Einstellungen (Standards werden wiederhergestellt)',
    'Einstellungen an Auflagern (Standards werden wiederhergestellt)',
    'Bauteilnamen werden neu vergeben',
  ]

  if (!wall1 || !wall2) {
    return <></>
  }

  return (
    <>
      <Form
        id={'MergeWallForm'}
        key={`${wall1_guid}-${wall2_guid}`}
        defaultValues={defaultValues}
        validationSchema={mergeWallSchema}
        onSubmit={handleSubmit}
      >
        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          size="small"
          loading={isLoading}
          startIcon={<MergeTypeIcon />}
        >
          Zusammenführen
        </LoadingButton>
        <Errors />
      </Form>

      <DialogBase
        open={showConfirmDialog}
        onClose={handleCancel}
        title="Wände zusammenführen bestätigen"
      >
        <>
          <Typography variant="body1" gutterBottom>
            Möchten Sie die Wände wirklich zusammenführen?
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Folgende Einstellungen werden zurückgesetzt:
          </Typography>
          <List dense>
            {sideEffects.map((effect, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={`${index + 1}. ${effect}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </>
        <>
          <Button onClick={handleCancel}>Abbrechen</Button>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            autoFocus
          >
            Bestätigen
          </LoadingButton>
        </>
      </DialogBase>
    </>
  )
}

export default MergeWallForm
